import { GoogleMap, Marker } from "@react-google-maps/api";
import React, { useEffect, useRef, useState } from "react";

import { QUERY_RADIUS, getBoundsRadius } from "../../api/locations";
import {
    LocationID,
    LocationProximity,
    isoLocationID,
    toGoogleLatLng,
} from "../../models";
import { mapPin2024D } from "../Svg";
import { getGlyph } from "./IndexGlyph";

import colorVariables from "../../styles/colors-2024.module.scss";
import styles from "./LocationListItem.module.scss";

const containerStyle = {
    width: "100%",
    height: "100%",
};

export const MarkerMap = (props: {
    locations: LocationProximity[];
    focusedLocationID: LocationID | null;
    setFocusedLocationID: (rlid: LocationID | null) => void;
    bounds?: google.maps.LatLngBounds;
}) => {
    const mapRef = useRef<google.maps.Map | null>(null);

    const [mapReady, setMapReady] = useState(false);

    const focusedLocation =
        props.locations.find((locProx) => {
            return locProx.location.id === props.focusedLocationID;
        }) || (props.locations.length >= 1 ? props.locations[0] : null);

    useEffect(() => {
        if (!mapRef.current || !mapReady || !focusedLocation) return;
        mapRef.current.panTo(toGoogleLatLng(focusedLocation.location.location));
    }, [mapReady, props.focusedLocationID, props.locations]);

    useEffect(() => {
        // Zoom the map appropriately
        if (props.bounds && mapReady) {
            const radius = getBoundsRadius(props.bounds);
            // Maps doesn't provide an easy way to, I dunno, `getFitBoundsValue`
            // without actually zooming, so a guard is in place to make sure it
            // never zooms in too much
            if (radius > QUERY_RADIUS) {
                mapRef.current!.fitBounds(props.bounds);
            } else {
                mapRef.current!.setZoom(10);
            }
        }
    }, [mapReady, props.bounds]);

    return (
        <GoogleMap
            mapContainerStyle={containerStyle}
            zoom={10}
            onLoad={(map) => {
                mapRef.current = map;
                setMapReady(true);
            }}
        >
            {props.locations.map(({ location }, index) => (
                <Marker
                    key={`${isoLocationID.unwrap(location.id)}`}
                    label={{
                        text: getGlyph(index),
                        className: styles.markerGlyph,
                    }}
                    position={toGoogleLatLng(location.location)}
                    onClick={() => {
                        props.setFocusedLocationID(location.id);
                    }}
                    icon={{
                        path: mapPin2024D,
                        fillColor: colorVariables.white,
                        fillOpacity: 1,
                        scale: 0.67,
                        strokeColor: colorVariables.red,
                        strokeWeight: 2,
                        labelOrigin: new google.maps.Point(21.5, 23),
                    }}
                />
            ))}
        </GoogleMap>
    );
};
